import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { chatAPI, ReviewSession } from '../../services/api';
import SharedChatView from '../Chat/SharedChatView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`review-tabpanel-${index}`}
      aria-labelledby={`review-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `review-tab-${index}`,
    'aria-controls': `review-tabpanel-${index}`,
  };
}

const ReviewInterface: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [reviewSessions, setReviewSessions] = useState<ReviewSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<'issue' | 'pending' | 'resolved'>('pending');
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedNoteType, setSelectedNoteType] = useState<string | null>(null);

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });
  
  const [statusCounts, setStatusCounts] = useState({
    issue: 0,
    pending: 0,
    resolved: 0,
  });

  const [noteTypeCounts, setNoteTypeCounts] = useState({
    general: 0,
    problem: 0,
    suggestion: 0,
    knowledgebase: 0,
  });

  const [previewModal, setPreviewModal] = useState({
    open: false,
    shareToken: '',
    sessionTitle: '',
  });

  const statusFilters = [
    { value: 'issue', label: 'Issues', color: '#dc2626' },
    { value: 'pending', label: 'Pending', color: '#d97706' },
    { value: 'resolved', label: 'Resolved', color: '#059669' },
  ];

  const currentStatus = statusFilters[currentTab]?.value;

  useEffect(() => {
    fetchReviewSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, pagination.page, selectedNoteType]);

  useEffect(() => {
    const handleFocus = () => {
      // Refresh data when user returns to this tab/window
      fetchReviewSessions();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReviewSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch sessions for current status and note type filter
      const response = await chatAPI.getReviewSessions(
        currentStatus,
        pagination.page,
        pagination.pageSize,
        selectedNoteType || undefined
      );
      setReviewSessions(response.results);
      setPagination(prev => ({ ...prev, count: response.count }));
      
      // Fetch counts for all statuses to update tab badges
      const [issueResponse, pendingResponse, resolvedResponse] = await Promise.all([
        chatAPI.getReviewSessions('issue', 1, 1, selectedNoteType || undefined),
        chatAPI.getReviewSessions('pending', 1, 1, selectedNoteType || undefined),
        chatAPI.getReviewSessions('resolved', 1, 1, selectedNoteType || undefined),
      ]);
      
      setStatusCounts({
        issue: issueResponse.count,
        pending: pendingResponse.count,
        resolved: resolvedResponse.count,
      });

      // Fetch counts for all note types to update filter badges (for current status)
      const [generalResponse, problemResponse, suggestionResponse, knowledgebaseResponse] = await Promise.all([
        chatAPI.getReviewSessions(currentStatus, 1, 1, 'general'),
        chatAPI.getReviewSessions(currentStatus, 1, 1, 'problem'),
        chatAPI.getReviewSessions(currentStatus, 1, 1, 'suggestion'),
        chatAPI.getReviewSessions(currentStatus, 1, 1, 'knowledgebase'),
      ]);
      
      setNoteTypeCounts({
        general: generalResponse.count,
        problem: problemResponse.count,
        suggestion: suggestionResponse.count,
        knowledgebase: knowledgebaseResponse.count,
      });
    } catch (err: any) {
      console.error('Error fetching review sessions:', err);
      setError(err.response?.data?.detail || 'Failed to fetch review sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedSessions([]); // Clear selection when switching tabs
    setSelectedNoteType(null); // Clear note type filter when switching tabs
  };

  const handleNoteTypeFilter = (noteType: string | null) => {
    setSelectedNoteType(noteType);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedSessions([]); // Clear selection when changing filter
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination(prev => ({ ...prev, page: value }));
    setSelectedSessions([]); // Clear selection when changing pages
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allSessionIds = reviewSessions.map(session => session.session_id);
      setSelectedSessions(allSessionIds);
    } else {
      setSelectedSessions([]);
    }
  };

  const handleSelectSession = (sessionId: string) => {
    setSelectedSessions(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  };

  const handleBulkUpdateStatus = async () => {
    if (selectedSessions.length === 0) {
      setError('Please select at least one session');
      return;
    }

    try {
      setBulkUpdateLoading(true);
      setError(null);
      
      const result = await chatAPI.bulkUpdateReviewStatus(selectedSessions, bulkStatus);
      
      if (result.failed_count > 0) {
        setError(`Updated ${result.updated_count} session(s). Failed: ${result.failed_count}. ${result.errors.join(', ')}`);
      } else {
        setSuccessMessage(`Successfully updated ${result.updated_count} session(s) to ${bulkStatus}`);
      }
      
      // Clear selection and refresh
      setSelectedSessions([]);
      await fetchReviewSessions();
      
    } catch (err: any) {
      console.error('Error bulk updating review status:', err);
      setError(err.response?.data?.detail || 'Failed to bulk update review status');
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  const handleViewSharedSession = async (sessionId: string, sessionTitle: string) => {
    if (!sessionId || sessionId === 'undefined') {
      console.error('Invalid session ID:', sessionId);
      setError('Invalid session ID');
      return;
    }

    try {
      // Use admin share endpoint to get share token for any user's session
      const shareResponse = await chatAPI.adminShareSession(sessionId);
      // Extract the share token from the URL
      const shareUrl = shareResponse.share_url;
      const shareToken = shareUrl.split('/').pop();
      
      // Open the preview modal
      setPreviewModal({
        open: true,
        shareToken: shareToken || '',
        sessionTitle: sessionTitle,
      });
    } catch (err: any) {
      console.error('Error sharing session:', err);
      setError(err.response?.data?.detail || 'Failed to share session');
    }
  };

  const handleClosePreview = () => {
    setPreviewModal({
      open: false,
      shareToken: '',
      sessionTitle: '',
    });
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'issue': return 'error';
      case 'pending': return 'warning';
      case 'resolved': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      p: 3,
      overflow: 'auto',
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
          Review Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage user sessions with notes and reviews
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs for Status Filtering */}
      <Card sx={{ mb: 3, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="review status tabs"
            sx={{ px: 2 }}
          >
            {statusFilters.map((filter, index) => (
              <Tab 
                key={filter.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {filter.label}
                    <Chip 
                      label={statusCounts[filter.value as keyof typeof statusCounts] || 0}
                      size="small"
                      sx={{ 
                        backgroundColor: filter.color,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                }
                {...a11yProps(index)} 
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        {statusFilters.map((filter, index) => (
          <TabPanel key={filter.value} value={currentTab} index={index}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {filter.label} ({pagination.count})
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchReviewSessions}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                Refresh
              </Button>
            </Box>

            {/* Note Type Filter */}
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#475569' }}>
                Filter by Note Type:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`All (${pagination.count})`}
                  onClick={() => handleNoteTypeFilter(null)}
                  color={selectedNoteType === null ? 'primary' : 'default'}
                  variant={selectedNoteType === null ? 'filled' : 'outlined'}
                  sx={{ 
                    fontWeight: selectedNoteType === null ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: selectedNoteType === null ? undefined : 'rgba(37, 99, 235, 0.08)',
                    },
                  }}
                />
                <Chip
                  label={`General (${noteTypeCounts.general})`}
                  onClick={() => handleNoteTypeFilter('general')}
                  color={selectedNoteType === 'general' ? 'primary' : 'default'}
                  variant={selectedNoteType === 'general' ? 'filled' : 'outlined'}
                  sx={{ 
                    fontWeight: selectedNoteType === 'general' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: selectedNoteType === 'general' ? undefined : 'rgba(37, 99, 235, 0.08)',
                    },
                  }}
                />
                <Chip
                  label={`Problem (${noteTypeCounts.problem})`}
                  onClick={() => handleNoteTypeFilter('problem')}
                  color={selectedNoteType === 'problem' ? 'error' : 'default'}
                  variant={selectedNoteType === 'problem' ? 'filled' : 'outlined'}
                  sx={{ 
                    fontWeight: selectedNoteType === 'problem' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: selectedNoteType === 'problem' ? undefined : 'rgba(220, 38, 38, 0.08)',
                    },
                  }}
                />
                <Chip
                  label={`Suggestion (${noteTypeCounts.suggestion})`}
                  onClick={() => handleNoteTypeFilter('suggestion')}
                  color={selectedNoteType === 'suggestion' ? 'success' : 'default'}
                  variant={selectedNoteType === 'suggestion' ? 'filled' : 'outlined'}
                  sx={{ 
                    fontWeight: selectedNoteType === 'suggestion' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: selectedNoteType === 'suggestion' ? undefined : 'rgba(5, 150, 105, 0.08)',
                    },
                  }}
                />
                <Chip
                  label={`Knowledgebase (${noteTypeCounts.knowledgebase})`}
                  onClick={() => handleNoteTypeFilter('knowledgebase')}
                  color={selectedNoteType === 'knowledgebase' ? 'info' : 'default'}
                  variant={selectedNoteType === 'knowledgebase' ? 'filled' : 'outlined'}
                  sx={{ 
                    fontWeight: selectedNoteType === 'knowledgebase' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: selectedNoteType === 'knowledgebase' ? undefined : 'rgba(3, 105, 161, 0.08)',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Bulk Action Toolbar */}
            {selectedSessions.length > 0 && (
              <Toolbar
                sx={{
                  pl: 2,
                  pr: 2,
                  mb: 2,
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  borderRadius: 2,
                  border: '1px solid rgba(37, 99, 235, 0.2)',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{ flex: '1 1 100%', fontWeight: 600, color: 'primary.main' }}
                  variant="subtitle1"
                  component="div"
                >
                  {selectedSessions.length} selected
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150, mr: 2 }}>
                  <InputLabel>Change Status To</InputLabel>
                  <Select
                    value={bulkStatus}
                    label="Change Status To"
                    onChange={(e) => setBulkStatus(e.target.value as 'issue' | 'pending' | 'resolved')}
                    sx={{ backgroundColor: 'white' }}
                  >
                    <MenuItem value="issue">Issue</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleBulkUpdateStatus}
                  disabled={bulkUpdateLoading}
                  sx={{
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    },
                  }}
                >
                  {bulkUpdateLoading ? 'Updating...' : 'Update Status'}
                </Button>
              </Toolbar>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : reviewSessions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No sessions found with {filter.label.toLowerCase()} status
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer 
                  sx={{ 
                    maxHeight: 'calc(100vh - 450px)',
                    minHeight: '300px',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f5f9',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#cbd5e1',
                      borderRadius: '4px',
                      '&:hover': {
                        background: '#94a3b8',
                      },
                    },
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ fontWeight: 600 }}>
                          <Checkbox
                            indeterminate={selectedSessions.length > 0 && selectedSessions.length < reviewSessions.length}
                            checked={reviewSessions.length > 0 && selectedSessions.length === reviewSessions.length}
                            onChange={handleSelectAll}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Session</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reviews</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Last Activity</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reviewSessions.map((session) => {
                        const isSelected = selectedSessions.includes(session.session_id);
                        return (
                        <TableRow 
                          key={session.session_id} 
                          hover 
                          selected={isSelected}
                          sx={{ 
                            cursor: 'pointer',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(37, 99, 235, 0.08)',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'rgba(37, 99, 235, 0.12)',
                            },
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectSession(session.session_id)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {session.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {session.session_id}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {session.user_username}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={session.review_status.charAt(0).toUpperCase() + session.review_status.slice(1)}
                              color={getStatusColor(session.review_status) as any}
                              size="small"
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {session.review_count} note{session.review_count !== 1 ? 's' : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(session.last_activity)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewSharedSession(session.session_id, session.title)}
                                color="primary"
                              >
                                View Chat Session
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {Math.ceil(pagination.count / pagination.pageSize) > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2, flexShrink: 0 }}>
                    <Pagination
                      count={Math.ceil(pagination.count / pagination.pageSize)}
                      page={pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </TabPanel>
        ))}
      </Card>

      {/* Preview Modal */}
      <Dialog
        open={previewModal.open}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          color: 'white',
          py: 2,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <b>Session History</b>
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClosePreview}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {previewModal.shareToken && (
            <SharedChatView shareToken={previewModal.shareToken} isModal={true} />
          )}
        </DialogContent>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success" 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default ReviewInterface; 